/**
 * Botón «generar PDF».
 *
 * El PDF no se genera en la petición: se pide, el worker lo hace y esta
 * pantalla pregunta hasta que está. Por eso el botón tiene tres estados y
 * ninguno es un reloj de arena sin más — el que espera necesita saber si
 * está tardando o si ha fallado.
 *
 * Si el sistema no tiene almacén configurado, el servidor lo dice y aquí
 * se cuenta tal cual: prometer un PDF que no se va a poder guardar es peor
 * que decir que todavía no se puede.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { openDownload } from '../session/open-download.js';

interface JobState {
  jobId: string;
  status: 'PENDIENTE' | 'EN_CURSO' | 'HECHO' | 'ERROR';
  ready: boolean;
  attempts?: number;
  error?: string | null;
}

interface Props {
  kind: 'ACTA' | 'INVOICE' | 'DELIVERY_NOTE' | 'QUOTE' | 'CONTRACT' | 'SERVICE_CONTRACT' | 'ANOMALY_REPORT' | 'MAINTENANCE_CERTIFICATE' | 'CHECKLIST_REPORT';
  entityId: string;
  /** Sello del acta o huella de la factura. */
  sourceHash: string;
  label?: string;
}

/** Cada cuánto se pregunta. Un PDF tarda segundos, no minutos. */
const POLL_MS = 1_500;

export function PdfButton({ kind, entityId, sourceHash, label }: Props): React.ReactNode {
  const { api } = useSession();
  const [job, setJob] = useState<JobState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);

  const poll = useCallback(
    async (jobId: string) => {
      try {
        const state = await api.get<JobState>(`/documents/jobs/${jobId}`);
        setJob(state);

        if (state.status === 'PENDIENTE' || state.status === 'EN_CURSO') {
          timer.current = window.setTimeout(() => void poll(jobId), POLL_MS);
        }
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'No se ha podido consultar el estado.');
      }
    },
    [api],
  );

  // Sin esto, salir de la pantalla deja el temporizador corriendo y
  // preguntando por un documento que ya nadie mira.
  useEffect(() => {
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, []);

  async function request(rehacer = false): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const state = await api.post<JobState>(
        `/documents/${kind}/${entityId}/${encodeURIComponent(sourceHash)}` +
          (rehacer ? '?force=1' : ''),
      );
      setJob(state);
      if (!state.ready) void poll(state.jobId);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido pedir el documento.');
    } finally {
      setBusy(false);
    }
  }

  // El servidor manda el PDF con `Content-Disposition: inline`, así que se abre
  // en el visor del navegador: desde ahí se previsualiza y se imprime. El botón
  // decía «Descargar», que es lo que NO hace.
  if (job?.status === 'HECHO') {
    return (
      <>
        {/* Botón y no enlace: un enlace abre la pestaña sin pasar por el
            cliente, y si la cookie de sesión ha caducado el usuario recibe un
            401 en crudo donde esperaba su documento. */}
        <button
          type="button"
          className="button button--primary button--small"
          onClick={() => void openDownload(api, `/v1/documents/jobs/${job.jobId}/download`)}
        >
          Ver e imprimir
        </button>{' '}
        {/* El PDF se guarda y se reutiliza. Eso está bien mientras nada cambie,
            pero hay cosas que sí cambian después —el logotipo de la empresa,
            sin ir más lejos— y entonces hace falta poder rehacerlo. */}
        <button
          type="button"
          className="button button--quiet button--small"
          onClick={() => void request(true)}
          disabled={busy}
        >
          {busy ? 'Rehaciendo…' : 'Volver a generar'}
        </button>
      </>
    );
  }

  return (
    <>
      <button
        className="button button--quiet button--small"
        type="button"
        disabled={busy || job?.status === 'PENDIENTE' || job?.status === 'EN_CURSO'}
        onClick={() => void request()}
      >
        {job?.status === 'PENDIENTE' || job?.status === 'EN_CURSO'
          ? 'Generando el PDF…'
          : (label ?? 'Generar PDF')}
      </button>

      {job?.status === 'ERROR' && (
        <span className="lines__warn">
          {/* El motivo, no «ha fallado»: sin él hay que llamar a la
              oficina para que alguien mire los registros del servidor. */}
          ⚠ No se pudo generar{job.error ? `: ${job.error}` : ''}. Se reintentará solo.
        </span>
      )}

      {error !== null && <span className="lines__warn">⚠ {error}</span>}
    </>
  );
}
